import {useCallback, useMemo, useRef, useReducer} from "react";
import {annotationEditorReducer, initialAnnotationEditor} from "./AnnotationEditorState.ts";
import {useEncounterAnnotations} from "@/encounter/annotations/useEncounterAnnotations.ts";
import type {EncounterAnnotation} from "@/encounter/model/EncounterAnnotation.ts";
import type {Dungeon} from "@/dungeons/model/Dungeon.ts";
import type {Annotation} from "@/dungeons/model/DungeonGeometry.ts";
import {gridToImage, worldToGrid} from "@/dungeons/geometry/Coordinates.ts";
import type {MapAnnotation} from "./MapAnnotation.ts";
import type {MapFeatureSelectionController} from "./useMapFeatureSelection.ts";

type DungeonLevel = Dungeon["levels"][number];

export default function useGameMasterAnnotations({encounterId, activeLevel, mapSelection, onPlaced}: {
    encounterId: string;
    activeLevel?: DungeonLevel;
    mapSelection: MapFeatureSelectionController;
    onPlaced: () => void;
}) {
    const {annotations: encounterAnnotations, save, remove, error: loadError} = useEncounterAnnotations(encounterId);
    const [editor, dispatch] = useReducer(annotationEditorReducer, initialAnnotationEditor);
    const {draft, editing, operation} = editor;
    const busy = operation.status === "saving" || operation.status === "deleting";
    const mutationError = operation.status === "failed" ? operation.message : undefined;
    const setEditing = (editing: boolean) => dispatch({type: "editing", editing});
    const interactionVersion = useRef(0);
    const {selection, selectAnnotation} = mapSelection;

    const annotations: MapAnnotation[] = useMemo(() => activeLevel ? [
        ...activeLevel.geometry.annotations.map(annotation => ({source: "dungeon" as const, annotation})),
        ...encounterAnnotations.filter(annotation => annotation.levelId === activeLevel.id)
            .map(annotation => ({source: "encounter" as const, annotation})),
        ...(draft?.levelId === activeLevel.id ? [{source: "encounter" as const, annotation: draft}] : []),
    ] : [], [activeLevel, encounterAnnotations, draft]);
    const selected = selection?.type === "annotation" && selection.levelId === activeLevel?.id
        ? annotations.find(item => item.source === selection.source && item.annotation.id === selection.id)
        : undefined;

    const close = useCallback(() => {
        interactionVersion.current += 1;
        dispatch({type: "reset"});
    }, []);
    const select = (item: MapAnnotation) => {
        if (!activeLevel) return;
        const alreadySelected = selection?.type === "annotation" && selection.levelId === activeLevel.id
            && selection.source === item.source && selection.id === item.annotation.id;
        if (!alreadySelected) {
            interactionVersion.current += 1;
            dispatch({type: "select", editing: item.annotation.id === draft?.id});
        }
        dispatch({type: "clearError"});
        selectAnnotation(activeLevel.id, item);
    };
    const place = (world: {x: number; y: number}) => {
        if (!activeLevel) return;
        const position = worldToGrid(world, activeLevel.geometry.grid);
        const image = gridToImage(position, activeLevel.geometry.registration);
        if (image.x < 0 || image.y < 0 || image.x > activeLevel.map.image.width || image.y > activeLevel.map.image.height) return;
        const annotation: EncounterAnnotation = {
            id: crypto.randomUUID(), levelId: activeLevel.id, position, kind: "NOTE", title: "Neue Notiz", text: null,
        };
        interactionVersion.current += 1;
        dispatch({type: "place", draft: annotation});
        selectAnnotation(activeLevel.id, {source: "encounter", annotation});
        onPlaced();
    };
    const saveAnnotation = async (annotation: Annotation) => {
        if (!activeLevel) return;
        const version = ++interactionVersion.current;
        dispatch({type: "start", status: "saving"});
        try {
            await save({...annotation, levelId: activeLevel.id});
            if (version === interactionVersion.current) {
                dispatch({type: "reset"});
            }
        } catch {
            if (version === interactionVersion.current) {
                dispatch({type: "fail", message: "Die Annotation konnte nicht gespeichert werden."});
            }
        } finally {
            if (version === interactionVersion.current) dispatch({type: "finish"});
        }
    };
    const move = (item: MapAnnotation, position: Annotation["position"]) => {
        if (item.source !== "encounter" || !activeLevel) return;
        const annotation = {...item.annotation, levelId: activeLevel.id, position};
        if (draft?.id === annotation.id) dispatch({type: "draft", draft: annotation});
        else void saveAnnotation(annotation);
    };
    const deleteSelected = async () => {
        if (!selected || selected.source !== "encounter") return;
        const version = ++interactionVersion.current;
        dispatch({type: "start", status: "deleting"});
        try {
            await remove(selected.annotation.id);
            if (version === interactionVersion.current) {
                close();
                mapSelection.clear();
            }
        } catch {
            // The HTTP client presents deletion failures as a toast.
        } finally {
            if (version === interactionVersion.current) dispatch({type: "finish"});
        }
    };

    return {annotations, selected, draft, editing, setEditing, mutationError, busy, loadError,
        close, select, place, save: saveAnnotation, move, deleteSelected};
}

export type GameMasterAnnotationsController = ReturnType<typeof useGameMasterAnnotations>;
